import {
  LOW_STOCK,
  OUT_OF_STOCK,
} from '@jouzen/ecom-components/v3/consts/stockStatus';
import type { RingSizeData } from '@jouzen/ecom-components/v3/types/ringSizeData';
import { useEffect, useRef, useState } from 'react';

import { usePDPContext } from '@/app/[locale]/product/_contexts/PDPProvider';
import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';
import useLegacyGen3InventoryStructure, {
  type Gen3Finish,
  type Gen3Style,
} from '@/app/[locale]/product/_hooks/useLegacyGen3InventoryStructure';
import type { UseWizardState } from '@/app/[locale]/product/_hooks/useWizardState/types';
import SizeSelection from '@/app/[locale]/product/_steps/enums/SizeStep';
import { SIZING_KIT } from '@/app/[locale]/store/_consts';

export interface UseSizeStepReturn extends UseWizardState {
  isProductSuccess: boolean;
  ringSizeData: RingSizeData[] | undefined;
  selectedSize: string;
  sizeModal: boolean;
  subtextHref: React.RefObject<HTMLDivElement | null>;
  onSelectSize: (value: string) => void;
  setSizeModal: (value: boolean) => void;
}

const useSizeStep = (): UseSizeStepReturn => {
  const { currentRoute, setSizingSelector, stockStatus, ...rest } =
    useWizardStateContext();
  const { isSuccess: isProductSuccess } = usePDPContext();
  const [sizeModal, setSizeModal] = useState<boolean>(false);
  const { accessory, isAccessory, isReady, size, finish, style } = currentRoute;
  const [selectedSize, setSelectedSize] = useState<string>(size.value);
  const subtextHref = useRef<HTMLDivElement>(null);
  //TODO: GROW-1607 Clean up Gen3
  const legacyGen3InventoryStructure = useLegacyGen3InventoryStructure();

  const ringSizeData =
    !isAccessory && finish.isValid && style.isValid
      ? legacyGen3InventoryStructure[style.value as Gen3Style][
          finish.value as Gen3Finish
        ].sizes
      : undefined;

  useEffect(() => {
    if (size.value !== SIZING_KIT) {
      setSizingSelector(SizeSelection.ChooseSize);
    } else {
      setSizingSelector(SizeSelection.SizingKit);
    }
    if (
      (stockStatus === OUT_OF_STOCK || stockStatus === LOW_STOCK) &&
      subtextHref.current
    ) {
      subtextHref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [setSizingSelector, size.value, stockStatus]);

  const handleSelectSize = (value: string) => {
    /* eagerly update state for fast ButtonV3 clicks
     * updating query params on charger-set is slower
     * than shallow route updates for rings
     */
    if (accessory.isValid && isReady) {
      currentRoute.pushAccessorySize(value);
    } else {
      currentRoute.pushSize(value);
    }
    setSelectedSize(value);
  };

  return {
    ...rest,
    currentRoute,
    isAccessory,
    isProductSuccess,
    ringSizeData,
    selectedSize,
    sizeModal,
    stockStatus,
    subtextHref,
    onSelectSize: handleSelectSize,
    setSizeModal,
    setSizingSelector,
  };
};

export default useSizeStep;
